import React, { useMemo } from 'react';
import Select from 'react-select';
import styled from '@emotion/styled';
import { Theme } from 'ui_system/theme';
import PropTypes from 'prop-types';

const ArrowStyles = styled.span`
  font-size: 21px;
  color: ${Theme.colors.iconsNotLinkedColor};
  padding: 6px;
  padding-bottom: 8px;
  padding-right: 8px;
  margin: auto;
`;

function DownArrow() {
  return <ArrowStyles data-testid="select-down-arrow">&#9662;</ArrowStyles>;
}

export function BaseSelect(props) {
  const {
    value: valueProp,
    components,
    styles: stylesProp,
    placeholder,
    isSearchable,
    isClearable,
    outOfRange,
    ...otherProps
  } = props;
  const styles = stylesProp || {};
  let value = valueProp;
  const { option, container, control, menuList, valueContainer, singleValue } =
    styles;

  if (typeof value !== 'object' && value !== undefined) {
    value = { value, label: value };
  }

  let customComponents = {
    DropdownIndicator: DownArrow,
    IndicatorSeparator: () => null,
  };
  if (components) customComponents = { ...customComponents, ...components };

  // Docs on react-select styles: https://react-select.com/styles
  const customStyles = useMemo(
    () => ({
      // Example of adding custom width to container:
      // styles={{ container: state => ({ width: 120 }) }}
      container: (base, state) => {
        const baseContainer = {
          ...base,
          width: 'initial',
          fontSize: 14,
        };
        return container
          ? { ...baseContainer, ...container(state) }
          : baseContainer;
      },
      control: (base, state) => {
        const baseControl = {
          ...base,
          cursor: 'pointer',
          // color: "#333333",
          // background: "#023950",
          // match with the menu
          borderRadius: 0,
          height: 44,

          // Overwrittes the different states of border
          borderColor: outOfRange
            ? Theme.colors.red
            : Theme.colors.inputs.border,
          // Removes weird border around container
          boxShadow: null,
          '&:hover': {
            // Overwrittes the different states of border
            borderColor: Theme.colors.borderActionableFieldColor,
          },
        };
        return control ? { ...baseControl, ...control(state) } : baseControl;
      },
      valueContainer: (base, state) => {
        const baseValueContainer = { ...base, height: '100%' };

        return valueContainer
          ? { ...baseValueContainer, ...valueContainer(state) }
          : baseValueContainer;
      },
      singleValue: (base, state) => {
        const singleValueProps = { ...base };

        const { isDisabled } = state;
        if (isDisabled) {
          const disabledProps = {
            color: Theme.colors.inputs.disabled.color,
            backgroundColor: Theme.colors.inputs.disabled.backgroundColor,
          };
          Object.assign(singleValueProps, disabledProps);
        }

        if (singleValue) {
          Object.assign(singleValueProps, singleValue(state));
        }

        return singleValueProps;
      },
      input: (base) => ({
        ...base,
        // hide blinking cursor
        color: isSearchable ? 'inherit' : 'transparent',
      }),
      menu: (base) => ({
        ...base,
        // override border radius to match the box
        borderRadius: 0,
        // beautify the word cut by adding a dash see https://caniuse.com/#search=hyphens for the compatibility
        // hyphens: 'auto',

        zIndex: 10000,

        // kill the gap
        marginTop: 0,
        textAlign: 'left',
        // prevent menu to scroll x
        wordWrap: 'break-word',
        borderBottomRightRadius: '5px',
        borderBottomLeftRadius: '5px',

        boxShadow: null,
      }),
      menuList: (base, state) => {
        const baseMenuList = {
          ...base,
          // kill the white space on first and last option
          // boxShadow: null,
          padding: 0,
          borderLeft: '1px solid #cccccc',
          borderRight: '1px solid #cccccc',
          borderBottom: '1px solid #cccccc',
        };
        return menuList
          ? { ...baseMenuList, ...menuList(state) }
          : baseMenuList;
      },
      option: (base, state) => {
        // This is how you would get the index currently:
        // let id = state.innerProps.id.slice(-1);
        // id = parseInt(id, 10);
        const { isSelected, isFocused } = state;
        const { isDisabled } = state.data;

        const baseOption = {
          ...base,
          cursor: isDisabled ? 'initial' : 'pointer',
          // borderLeft:
          //   isFocused || isSelected ? '1px solid #cccccc' : '1px solid transparent',
          background: isFocused ? '#ebf5ff' : isSelected ? '#f5faff' : 'white',
          color: isDisabled
            ? '#cccccc'
            : isSelected || isFocused
              ? Theme.colors.primaryTextColor
              : Theme.colors.secondaryTextColor,

          '&:active': { background: isDisabled ? null : '#b2d4ff' },
        };

        return option ? { ...baseOption, ...option(state) } : baseOption;
      },
    }),
    [styles, outOfRange]
  );

  return (
    <Select
      placeholder={
        typeof placeholder === 'undefined' ? 'Select...' : placeholder
      }
      styles={customStyles}
      components={customComponents}
      value={value}
      isSearchable={isSearchable || false}
      isClearable={isClearable || false}
      {...otherProps}
    />
  );
}

// 'value' can be an object ({value: v, label: l}) or a string
// (can only be a string if you are fine with the value and label being the same)
BaseSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
};
